const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let loadPromise: Promise<void> | null = null;

type TurnstileApi = {
  ready: (cb: () => void) => void;
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/**
 * Load Cloudflare Turnstile only when a form needs it (not on every page via layout).
 * Keeps the script off the critical rendering path for LCP/FCP.
 */
export function useTurnstileScript() {
  const load = (): Promise<void> => {
    if (import.meta.server) return Promise.resolve();
    if (typeof window !== "undefined" && window.turnstile) {
      return Promise.resolve();
    }
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${TURNSTILE_SRC}"]`
      );
      if (existing) {
        if (window.turnstile) {
          resolve();
          return;
        }
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener(
          "error",
          () => {
            loadPromise = null;
            reject(new Error("Failed to load Turnstile"));
          },
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        loadPromise = null;
        reject(new Error("Failed to load Turnstile"));
      };
      document.head.appendChild(script);
    });

    return loadPromise;
  };

  return { load };
}
